<template>
<p>&nbsp;</p>
  <div class="promote-page">
    <main class="promote-shell">
      <section v-if="!accessGranted" class="access-panel">
        <div class="access-icon"><vue-feather type="lock" size="26" /></div>
        <h1>{{ t('accessTitle') }}</h1>
        <p>{{ t('accessDescription') }}</p>
        <form @submit.prevent="submitAccessCode">
          <label for="access-code">{{ t('accessCode') }}</label>
          <input id="access-code" v-model="accessCode" type="text" autocomplete="off" autofocus />
          <p v-if="accessError" class="access-error">{{ t('accessDenied') }}</p>
          <button type="submit" class="access-button">{{ t('accessSubmit') }}</button>
        </form>
      </section>

      <template v-else>
      <header>
        <p class="eyebrow">{{ eventKey }}</p>
        <p class="subtitle">{{ t('subtitle') }}</p>
      </header>

      <section class="upload-panel" aria-label="Image upload">
        <div class="size-options">
          <span>{{ t('longEdge') }}</span>
          <label v-for="size in sizes" :key="size" :class="{ selected: longEdge === size }">
            <input v-model="longEdge" type="radio" :value="size" :disabled="isUploading" />
            {{ size }} px
          </label>
        </div>

        <label class="drop-zone" :class="{ disabled: isUploading }" for="promote-files">
          <input id="promote-files" type="file" accept="image/jpeg,image/png" multiple :disabled="isUploading" @change="queueFiles" />
          <vue-feather type="upload-cloud" size="20" />
          <strong>{{ t('selectImages') }}</strong>
          <span>{{ t('limit') }}</span>
        </label>

        <div v-if="queue.length" class="progress-area">
          <div class="progress-heading">
            <span>{{ t('processing') }} {{ completedCount }}/{{ queue.length }}</span>
            <span>{{ overallProgress }}%</span>
          </div>
          <div class="progress"><div :style="{ width: `${overallProgress}%` }"></div></div>
          <p v-if="activeFileName">{{ activeFileName }}</p>
        </div>
        <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
      </section>

      <section class="gallery-section">
        <div class="gallery-heading">
          <h2>{{ t('latest') }}</h2>
          <div class="gallery-actions">
            <button type="button" class="select-all-button" :disabled="!images.length || isZipping" @click="toggleSelectAll">
              {{ allImagesSelected ? t('clearSelection') : t('selectAll') }}
            </button>
            <label class="download-type">
              <span>{{ t('downloadType') }}</span>
              <select v-model="downloadType" :disabled="isZipping">
                <option value="processed">{{ t('logoShort') }}</option>
                <option value="raw">{{ t('rawShort') }}</option>
              </select>
            </label>
            <button type="button" class="zip-button" :disabled="!selectedImageIds.length || isZipping" @click="downloadSelectedAsZip">
              <vue-feather :type="isZipping ? 'loader' : 'download'" size="18" />
              {{ isZipping ? t('creatingZip') : `${t('downloadZip')} (${selectedImageIds.length})` }}
            </button>
            <button type="button" class="refresh-button" :title="t('refresh')" :disabled="isZipping" @click="loadImages">
              <vue-feather type="refresh-cw" size="18" />
            </button>
          </div>
        </div>
        <p v-if="isLoading" class="empty-message">{{ t('loading') }}</p>
        <p v-else-if="!images.length" class="empty-message">{{ t('empty') }}</p>
        <div v-else class="gallery">
          <article v-for="image in images" :key="image.id" class="gallery-item" :class="{ selected: selectedImageIds.includes(image.id) }">
            <a class="image-preview-button" :href="image.logo_url" :title="t('view')" @click.prevent="openPreview(image)">
              <img :src="image.logo_url" :alt="image.file_name" loading="lazy" />
            </a>
            <label class="image-select" :title="t('selectImage')">
              <input v-model="selectedImageIds" type="checkbox" :value="image.id" :disabled="isZipping" />
            </label>
            <span v-if="image.create_date" class="image-create-date">{{ formatDate(image.create_date) }}</span>
            <a
              class="download-button processed-download-button"
              :href="image.logo_url"
              :title="t('processedFile')"
              :class="{ disabled: downloadingIds.includes(`${image.id}-processed`) }"
              @click.prevent="downloadImage(image, 'processed')"
            >
              <vue-feather :type="downloadingIds.includes(`${image.id}-processed`) ? 'loader' : 'download'" size="15" />
              <span>{{ t('processedShort') }}</span>
            </a>
            <a
              class="download-button raw-download-button"
              :href="image.raw_url || image.logo_url"
              :title="t('rawFile')"
              :class="{ disabled: downloadingIds.includes(`${image.id}-raw`) || !image.raw_url }"
              @click.prevent="downloadImage(image, 'raw')"
            >
              <vue-feather :type="downloadingIds.includes(`${image.id}-raw`) ? 'loader' : 'download'" size="15" />
              <span>{{ t('rawShort') }}</span>
            </a>
          </article>
        </div>
        <div v-if="hasMore || isLoadingMore" ref="loadMoreTrigger" class="load-more-status">
          <span v-if="isLoadingMore">{{ t('loadingMore') }}</span>
        </div>
        <vue-easy-lightbox
          :visible="previewVisible"
          :imgs="previewImages"
          :index="previewIndex"
          :loop="true"
          @on-index-change="previewIndex = $event"
          @hide="previewVisible = false"
        />
        <div v-if="previewVisible" class="preview-download-actions">
          <button
            type="button"
            class="preview-download-button"
            :disabled="!previewCurrentImage || downloadingIds.includes(`${previewCurrentImage.id}-raw`)"
            @click="downloadImage(previewCurrentImage, 'raw')"
          >
            <vue-feather :type="previewCurrentImage && downloadingIds.includes(`${previewCurrentImage.id}-raw`) ? 'loader' : 'download'" size="15" />
            {{ t('rawShort') }}
          </button>
          <button
            type="button"
            class="preview-download-button"
            :disabled="!previewCurrentImage || downloadingIds.includes(`${previewCurrentImage.id}-processed`)"
            @click="downloadImage(previewCurrentImage, 'processed')"
          >
            <vue-feather :type="previewCurrentImage && downloadingIds.includes(`${previewCurrentImage.id}-processed`) ? 'loader' : 'download'" size="15" />
            {{ t('processedShort') }}
          </button>
        </div>
      </section>
      </template>
    </main>
  </div>
</template>

<script>
import axios from 'axios';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

const translations = {
  th: {
    accessTitle: 'เข้าสู่ระบบจัดการภาพ', accessDescription: 'กรอกรหัสเพื่อดำเนินการต่อ', accessCode: 'รหัสเข้าใช้งาน', accessSubmit: 'เข้าสู่หน้าใช้งาน', accessDenied: 'รหัสไม่ถูกต้อง กรุณาลองใหม่',
    subtitle: 'เลือกขนาด อัปโหลดภาพ แล้วระบบจะใส่กรอบงานให้อัตโนมัติ',
    longEdge: 'ขนาดภาพ', selectImages: 'เลือกรูปภาพ', limit: 'อัปโหลดได้สูงสุดครั้งละ 20 รูป',
    processing: 'กำลังประมวลผล', latest: 'ภาพล่าสุด', refresh: 'โหลดรายการใหม่', selectAll: 'เลือกทั้งหมด', clearSelection: 'ยกเลิกการเลือก', selectImage: 'เลือกภาพ', downloadZip: 'โหลด ZIP', creatingZip: 'กำลังสร้าง ZIP', downloadType: 'ชนิดไฟล์', processedFile: 'ไฟล์ที่จัดการแล้ว', logoShort: 'logo', processedShort: 'logo', rawFile: 'ไฟล์ต้นฉบับ', rawShort: 'raw', rawUnavailable: 'รายการนี้ไม่มีไฟล์ต้นฉบับ',
    loading: 'กำลังโหลดภาพ...', loadingMore: 'กำลังโหลดภาพเพิ่ม...', empty: 'ยังไม่มีภาพที่สร้าง', view: 'ดูภาพขนาดใหญ่', download: 'ดาวน์โหลดภาพ', downloadFailed: 'ไม่สามารถดาวน์โหลดภาพได้', zipFailed: 'ไม่สามารถสร้างไฟล์ ZIP ได้', uploadFailed: 'ไม่สามารถประมวลผลรูปภาพได้', tooMany: 'เลือกรูปได้สูงสุด 20 รูปต่อครั้ง',
  },
  en: {
    accessTitle: 'Access Image Manager', accessDescription: 'Enter the access code to continue.', accessCode: 'Access code', accessSubmit: 'Continue', accessDenied: 'Incorrect access code. Please try again.',
    subtitle: 'Upload images, choose a size, and apply the event frame automatically.',
    longEdge: 'Long edge', selectImages: 'Select images', limit: 'Up to 20 images per upload',
    processing: 'Processing', latest: 'Latest images', refresh: 'Refresh images', selectAll: 'Select all', clearSelection: 'Clear selection', selectImage: 'Select image', downloadZip: 'Download ZIP', creatingZip: 'Creating ZIP', downloadType: 'File type', processedFile: 'Processed file', logoShort: 'logo', processedShort: 'Processed', rawFile: 'Original file', rawShort: 'raw', rawUnavailable: 'The original file is unavailable for this image',
    loading: 'Loading images...', loadingMore: 'Loading more images...', empty: 'No generated images yet', view: 'View full image', download: 'Download image', downloadFailed: 'Unable to download image', zipFailed: 'Unable to create ZIP file', uploadFailed: 'Unable to process this image', tooMany: 'Select a maximum of 20 images per upload',
  },
};

export default {
  name: 'EventHighlightPromote',
  data() {
    return {
      sizes: [2048, 3000, 3500], longEdge: 2048, queue: [], completedCount: 0,
      activeProgress: 0, activeFileName: '', isUploading: false, isLoading: false,
      images: [], errorMessage: '', downloadingIds: [], selectedImageIds: [], downloadType: 'processed', isZipping: false, previewVisible: false, previewImage: '', previewIndex: 0,
      imageOffset: 0, imageLimit: 70, hasMore: false, totalImages: 0, isLoadingMore: false, imageObserver: null,
      canLoadNextPage: false,
      accessGranted: false, accessCode: '', accessError: false,
    };
  },
  computed: {
    eventKey() { return this.$route.params.event_key; },
    language() { return process.env.VUE_APP_LANGUAGE === 'EN' ? 'en' : 'th'; },
    accessCodeValue() { return process.env.VUE_APP_SYSTEM_PREFIX || ''; },
    overallProgress() {
      if (!this.queue.length) return 0;
      return Math.round(((this.completedCount + this.activeProgress / 100) / this.queue.length) * 100);
    },
    allImagesSelected() {
      return this.images.length > 0 && this.selectedImageIds.length === this.images.length;
    },
    accessStorageKey() {
      return `promote_access_${this.eventKey}`;
    },
    previewImages() {
      return this.images.map((image) => image.logo_url).filter(Boolean);
    },
    previewCurrentImage() {
      return this.images[this.previewIndex];
    },
  },
  mounted() {
    const accessExpiresAt = Number(localStorage.getItem(this.accessStorageKey));
    if (accessExpiresAt > Date.now()) {
      this.accessGranted = true;
    } else {
      localStorage.removeItem(this.accessStorageKey);
    }
    if (this.accessGranted) {
      this.loadImages(true);
      this.$nextTick(() => this.setupImageObserver());
    }
    window.addEventListener('scroll', this.enableNextPageLoad, { passive: true });
  },
  beforeUnmount() {
    if (this.imageObserver) this.imageObserver.disconnect();
    window.removeEventListener('scroll', this.enableNextPageLoad);
  },
  methods: {
    t(key) { return translations[this.language][key]; },
    submitAccessCode() {
      this.accessError = this.accessCode !== this.accessCodeValue;
      if (this.accessError) return;
      const threeDaysInMilliseconds = 3 * 24 * 60 * 60 * 1000;
      localStorage.setItem(this.accessStorageKey, String(Date.now() + threeDaysInMilliseconds));
      this.accessGranted = true;
      this.loadImages(true);
      this.$nextTick(() => this.setupImageObserver());
    },
    getDownloadUrl(imageUrl) {
      const separator = imageUrl.includes('?') ? '&' : '?';
      return `${imageUrl}${separator}cache_bust=${Date.now()}_${Math.random().toString(36).slice(2)}`;
    },
    getImageDownloadUrl(image) {
      return this.downloadType === 'raw' ? image.raw_url : image.logo_url;
    },
    getDownloadFileName(image, type = 'processed') {
      const originalName = image.file_name || `${type}_${image.original_file_name}`;
      return type === 'raw' ? originalName : `logo_${originalName}`;
    },
    toggleSelectAll() {
      this.selectedImageIds = this.allImagesSelected ? [] : this.images.map((image) => image.id);
    },
    setupImageObserver() {
      if (this.imageObserver) this.imageObserver.disconnect();
      this.imageObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadMoreImages();
      }, { rootMargin: '400px 0px' });
      this.observeImageTrigger();
    },
    observeImageTrigger() {
      if (this.imageObserver && this.$refs.loadMoreTrigger) {
        this.imageObserver.observe(this.$refs.loadMoreTrigger);
      }
    },
    enableNextPageLoad() {
      this.canLoadNextPage = true;
    },
    async loadMoreImages() {
      if (this.isLoading || this.isLoadingMore || !this.hasMore || !this.canLoadNextPage) return;
      this.canLoadNextPage = false;
      await this.loadImages(false);
    },
    async downloadSelectedAsZip() {
      const selectedImages = this.images.filter((image) => this.selectedImageIds.includes(image.id));
      if (!selectedImages.length || this.isZipping) return;
      this.isZipping = true;
      this.errorMessage = '';
      try {
        const zip = new JSZip();
        const names = new Set();
        for (const image of selectedImages) {
          const imageUrl = this.getImageDownloadUrl(image);
          if (!imageUrl) {
            this.errorMessage = this.t('rawUnavailable');
            return;
          }
          const response = await axios.get(this.getDownloadUrl(imageUrl), { responseType: 'blob' });
          const baseName = this.getDownloadFileName(image, this.downloadType);
          const extensionIndex = baseName.lastIndexOf('.');
          const name = extensionIndex > -1 ? baseName.slice(0, extensionIndex) : baseName;
          const extension = extensionIndex > -1 ? baseName.slice(extensionIndex) : '.jpg';
          let zipName = baseName;
          let copyNumber = 2;
          while (names.has(zipName)) {
            zipName = `${name} (${copyNumber})${extension}`;
            copyNumber += 1;
          }
          names.add(zipName);
          zip.file(zipName, response.data);
        }
        saveAs(await zip.generateAsync({ type: 'blob' }), `${this.eventKey}-promote.zip`);
      } catch (error) {
        this.errorMessage = this.t('zipFailed');
      } finally {
        this.isZipping = false;
      }
    },
    openPreview(image) {
      this.previewImage = image.logo_url;
      this.previewIndex = Math.max(this.images.findIndex((item) => item.id === image.id), 0);
      this.previewVisible = true;
    },
    formatDate(value) {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? value : date.toLocaleString(this.language === 'en' ? 'en-GB' : 'th-TH');
    },
    async downloadImage(image, type = 'processed') {
      const downloadId = `${image.id}-${type}`;
      if (this.downloadingIds.includes(downloadId)) return;
      const imageUrl = type === 'raw' ? image.raw_url : image.logo_url;
      if (!imageUrl) {
        this.errorMessage = this.t('rawUnavailable');
        return;
      }
      const trackProgress = !this.previewVisible;
      if (trackProgress) this.downloadingIds.push(downloadId);
      try {
        const response = await axios.get(this.getDownloadUrl(imageUrl), { responseType: 'blob' });
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = this.getDownloadFileName(image, type);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.errorMessage = this.t('downloadFailed');
      } finally {
        if (trackProgress) this.downloadingIds = this.downloadingIds.filter((id) => id !== downloadId);
      }
    },
    async loadImages(reset = true) {
      if (reset && this.isLoading) return;
      if (!reset && (this.isLoading || this.isLoadingMore)) return;
      if (reset) {
        this.isLoading = true;
        this.imageOffset = 0;
        this.canLoadNextPage = false;
      } else {
        this.isLoadingMore = true;
      }
      try {
        const response = await axios.get(`/v1/promote/${encodeURIComponent(this.eventKey)}?limit=${this.imageLimit}&offset=${this.imageOffset}`);
        const nextImages = response.data.body || [];
        this.images = reset ? nextImages : [...this.images, ...nextImages];
        this.imageOffset += nextImages.length;
        this.totalImages = Number(response.data.total || this.images.length);
        this.hasMore = response.data.has_more === true;
        this.selectedImageIds = this.selectedImageIds.filter((id) => this.images.some((image) => image.id === id));
      } catch (error) {
        this.errorMessage = this.t('uploadFailed');
      } finally {
        if (reset) this.isLoading = false;
        else this.isLoadingMore = false;
        this.$nextTick(() => this.observeImageTrigger());
      }
    },
    async queueFiles(event) {
      const files = Array.from(event.target.files || []);
      event.target.value = '';
      this.errorMessage = '';
      if (!files.length) return;
      if (files.length > 20) {
        this.errorMessage = this.t('tooMany');
        return;
      }
      this.queue = files;
      this.completedCount = 0;
      this.activeProgress = 0;
      this.isUploading = true;
      for (const file of files) {
        this.activeFileName = file.name;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('event_key', this.eventKey);
        formData.append('long_edge', String(this.longEdge));
        try {
          const response = await axios.post('/v1/promote/upload', formData, {
            onUploadProgress: (progressEvent) => {
              this.activeProgress = progressEvent.total ? Math.round((progressEvent.loaded / progressEvent.total) * 100) : 0;
            },
          });
          this.images.unshift({ id: response.data.id, file_name: response.data.file_name, logo_url: response.data.logo_file_name });
        } catch (error) {
          this.errorMessage = `${this.t('uploadFailed')}: ${file.name}`;
        }
        this.completedCount += 1;
        this.activeProgress = 0;
      }
      this.activeFileName = '';
      this.isUploading = false;
      await this.loadImages();
    },
  },
};
</script>

<style scoped>
.promote-page { --ink: #172b3a; --muted: #607080; --line: #d8e1e6; --teal: #5b9dff; --teal-dark: #0d6efd; --amber: #e9873d; min-height: 100vh; padding: 56px 24px 72px; color: var(--ink); background-color: #fff; font-family: 'Kanit', sans-serif; }
.promote-shell { max-width: 1160px; margin: 0 auto; }
.access-panel { max-width: 440px; margin: 72px auto; padding: 32px; background: #fff; border: 1px solid var(--line); border-radius: 8px; box-shadow: 0 16px 34px rgba(23, 43, 58, .1); text-align: center; } .access-icon { display: inline-grid; place-items: center; width: 52px; height: 52px; margin-bottom: 14px; color: #fff; background: var(--teal-dark); border-radius: 50%; } .access-panel h1 { margin: 0; font-size: 25px; font-weight: 600; } .access-panel > p { margin: 7px 0 24px; color: var(--muted); } .access-panel form { text-align: left; } .access-panel label { display: block; margin-bottom: 7px; color: var(--muted); font-size: 13px; } .access-panel input { width: 100%; height: 42px; padding: 8px 11px; border: 1px solid var(--line); border-radius: 5px; font: inherit; box-sizing: border-box; } .access-panel input:focus { outline: 0; border-color: var(--teal-dark); box-shadow: 0 0 0 3px rgba(13, 110, 253, .14); } .access-error { margin: 8px 0 0; color: #b2342b; font-size: 13px; } .access-button { width: 100%; min-height: 42px; margin-top: 18px; border: 0; border-radius: 5px; color: #fff; background: var(--teal-dark); font: inherit; font-weight: 500; }
.promote-shell > header { max-width: 720px; margin-bottom: 30px; }
.eyebrow { display: inline-flex; align-items: center; min-height: 38px; margin: 0 0 10px; padding: 5px 14px; color: var(--teal-dark); background: #e7f0ff; border: 1px solid #bed7ff; border-radius: 999px; font-size: 18px; font-weight: 600; }
h1, h2 { font-family: 'Kanit', sans-serif; letter-spacing: 0; margin: 0; } h1 { font-size: 38px; font-weight: 600; line-height: 1.2; } .subtitle { max-width: 650px; margin: 10px 0 0; color: var(--muted); font-size: 16px; }
.upload-panel { position: relative; padding: 28px; overflow: hidden; background: #fff; border: 1px solid var(--line); border-radius: 8px; box-shadow: 0 16px 34px rgba(23, 43, 58, .08); } .upload-panel::before { position: absolute; top: 0; left: 0; width: 100%; height: 4px; content: ''; background: var(--teal); }
.size-options { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; font-weight: 500; } .size-options > span { margin-right: 8px; color: var(--muted); font-size: 14px; }
.size-options label { min-width: 84px; border: 1px solid var(--line); border-radius: 5px; cursor: pointer; padding: 8px 12px; color: var(--muted); background: #fafcff; text-align: center; transition: background .16s, border-color .16s, color .16s; } .size-options label:hover { border-color: #a8ccff; color: var(--teal-dark); } .size-options label.selected { background: var(--teal); border-color: var(--teal); color: #fff; box-shadow: 0 4px 10px rgba(13, 110, 253, .18); } .size-options input { position: absolute; opacity: 0; }
.drop-zone { display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 8px; min-height: 134px; margin-top: 24px; color: var(--teal-dark); background: #f7faff; border: 2px dashed #a8ccff; border-radius: 6px; cursor: pointer; text-align: center; transition: background .16s, border-color .16s, transform .16s; } .drop-zone:hover { background: #eef5ff; border-color: var(--teal); transform: translateY(-1px); } .drop-zone :deep(svg) { padding: 8px; color: #fff; background: var(--teal); border-radius: 50%; box-sizing: content-box; } .drop-zone strong { font-size: 18px; font-weight: 600; } .drop-zone input { display: none; } .drop-zone span { color: var(--muted); font-size: 14px; } .drop-zone.disabled { cursor: wait; opacity: .6; transform: none; }
.progress-area { margin-top: 22px; padding: 14px 16px; background: #f7faff; border: 1px solid #dce9fb; border-radius: 5px; } .progress-heading, .gallery-heading { display: flex; justify-content: space-between; align-items: center; gap: 16px; } .progress-heading { font-size: 14px; font-weight: 500; } .progress { height: 8px; background: #dce9f8; border-radius: 999px; margin-top: 10px; overflow: hidden; } .progress div { height: 100%; background: var(--amber); border-radius: inherit; transition: width .2s; } .progress-area p { font-size: 13px; color: var(--muted); margin: 8px 0 0; word-break: break-word; } .error-message { color: #b2342b; margin: 14px 0 0; }
.gallery-section { margin-top: 44px; } h2 { font-size: 26px; font-weight: 600; } .gallery-actions { display: flex; align-items: center; gap: 8px; } .refresh-button { display: grid; place-items: center; width: 38px; height: 38px; border: 1px solid var(--line); border-radius: 5px; color: var(--teal-dark); background: #fff; transition: background .16s, border-color .16s; } .refresh-button:hover { border-color: var(--teal); background: #eef5ff; }
.select-all-button, .zip-button { display: inline-flex; align-items: center; gap: 7px; min-height: 38px; border: 1px solid var(--teal); border-radius: 5px; padding: 7px 12px; font-family: inherit; font-size: 13px; font-weight: 500; transition: background .16s, box-shadow .16s, transform .16s; } .select-all-button { color: var(--teal-dark); background: #fff; } .select-all-button:hover { background: #eef5ff; } .zip-button { color: #fff; background: var(--teal); box-shadow: 0 4px 10px rgba(13, 110, 253, .18); } .zip-button:hover { background: var(--teal-dark); transform: translateY(-1px); } .select-all-button:disabled, .zip-button:disabled, .refresh-button:disabled { cursor: not-allowed; opacity: .55; transform: none; }
.download-type { display: inline-flex; align-items: center; gap: 6px; min-height: 38px; color: var(--muted); font-size: 13px; } .download-type select { min-height: 38px; border: 1px solid var(--line); border-radius: 5px; padding: 6px 28px 6px 9px; color: var(--ink); background: #fff; font: inherit; cursor: pointer; }
.gallery { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 10px; margin-top: 18px; } .gallery-item { position: relative; min-width: 0; overflow: hidden; background: #e8f0fa; border: 1px solid #d6e2f0; border-radius: 6px; box-shadow: 0 7px 18px rgba(23, 43, 58, .08); transition: border-color .16s, box-shadow .16s, transform .16s; } .gallery-item:hover { border-color: #9fc5ff; box-shadow: 0 12px 26px rgba(23, 43, 58, .14); transform: translateY(-2px); } .gallery-item.selected { border: 2px solid var(--teal); box-shadow: 0 0 0 3px rgba(13, 110, 253, .16); }
.load-more-status { min-height: 34px; padding: 18px 0 4px; color: var(--muted); font-size: 13px; text-align: center; }
.image-preview-button { display: block; width: 100%; padding: 0; border: 0; background: transparent; cursor: pointer; } .gallery img { width: 100%; height: auto; display: block; }
.image-select { position: absolute; top: 10px; right: 10px; display: grid; place-items: center; width: 30px; height: 30px; background: #fff; border: 1px solid #d8e1e6; border-radius: 4px; box-shadow: 0 2px 7px rgba(23, 43, 58, .2); cursor: pointer; } .image-select input { width: 16px; height: 16px; cursor: pointer; accent-color: var(--teal-dark); } .image-create-date { position: absolute; top: 10px; left: 10px; padding: 4px 7px; color: #fff; background: rgba(23, 43, 58, .86); border-radius: 3px; font-size: 11px; line-height: 1.2; }
.download-button { position: absolute; bottom: 10px; display: inline-flex; align-items: center; justify-content: center; gap: 4px; min-width: 38px; height: 38px; padding: 0 8px; color: #fff; background: rgba(23, 43, 58, .88); border-radius: 4px; box-shadow: 0 3px 8px rgba(23, 43, 58, .24); font-size: 11px; font-weight: 600; transition: background .16s, transform .16s; } .processed-download-button { right: 10px; } .raw-download-button { left: 10px; } .download-button:hover { color: #fff; background: var(--teal); transform: translateY(-1px); } .download-button.disabled { cursor: wait; opacity: .7; }
.preview-download-actions { position: fixed; bottom: 24px; left: 50%; z-index: 10001; display: flex; align-items: center; gap: 12px; transform: translateX(-50%); } .preview-download-button { display: inline-flex; align-items: center; justify-content: center; gap: 7px; min-width: 92px; min-height: 44px; margin: 0; padding: 8px 16px; border: 1px solid rgba(255, 255, 255, .75); border-radius: 5px; color: #fff; background: rgba(23, 43, 58, .88); font: inherit; font-size: 15px; font-weight: 600; cursor: pointer; } .preview-download-button:hover { background: var(--teal); } .preview-download-button:disabled { cursor: wait; opacity: .65; }
.empty-message { padding: 34px 0; color: var(--muted); } @media (max-width: 960px) { .gallery { grid-template-columns: repeat(4, minmax(0, 1fr)); } } @media (max-width: 760px) { .gallery { grid-template-columns: repeat(3, minmax(0, 1fr)); } } @media (max-width: 640px) { .promote-page { padding: 32px 14px 48px; } h1 { font-size: 30px; } .upload-panel { padding: 20px; } .gallery-heading { align-items: flex-start; } .gallery-actions { flex-wrap: wrap; justify-content: flex-end; } .gallery { grid-template-columns: repeat(2, minmax(0, 1fr)); } .image-create-date { top: 7px; left: 7px; font-size: 10px; } .image-select { top: 7px; right: 7px; width: 27px; height: 27px; } .download-button { bottom: 7px; height: 34px; padding: 0 6px; } .processed-download-button { right: 7px; } .raw-download-button { left: 7px; } }
</style>